import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'
import { Actions, ofType } from '@ngrx/effects'
import { tapResponse } from '@ngrx/operators'
import { Store } from '@ngrx/store'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import { RafflesApiService } from 'libs/gb-dashboard/raffle/data-access/src/lib/+state/raffles-api.service'
import * as RafflesActions from 'libs/gb-dashboard/raffle/data-access/src/lib/+state/raffles.actions'
import { RaffleDrawsEntity } from 'libs/gb-dashboard/raffle/data-access/src/lib/+state/raffles.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { GbPaginatedResponse } from 'libs/shared/models/shared.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { from, of, Subject } from 'rxjs'
import {
  buffer,
  concatMap,
  debounceTime,
  delay,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators'

interface State {
  results: RaffleDrawsEntity[]
  loading: boolean
  error?: HttpErrorResponse
  count: number
  offset: number
}

@Injectable({
  providedIn: 'root',
})
export class GbRaffleDrawStore extends ComponentStore<State> {
  readonly loading$ = this.select((state) => state.loading)
  readonly count$ = this.select((state) => state.count)
  readonly offset$ = this.select((state) => state.offset)
  private actions$ = inject(Actions)
  readonly raffleDraws$ = this.select((state) => state.results)
  readonly LOADER_ID = 'raffleDrawAction'

  store = inject(Store)
  rafflesApiService = inject(RafflesApiService)
  selectedAuctionFacade = inject(SelectedAuctionFacade)
  gbNotificationService = inject(GbNotificationService)
  gbErrorMessageService = inject(GbErrorMessageService)
  gbLoaderService = inject(GbLoaderService)
  appConfig = inject<EnvironmentModel>(APP_CONFIG)

  constructor() {
    super({ offset: 0, results: [], loading: false, count: 0 })
  }

  /** -------------------------
   * Pure state updaters
   * ------------------------- */
  upsert(draw: RaffleDrawsEntity) {
    this.patchState((state) => ({
      ...state,
      results: [...state.results.filter((x) => x.uuid !== draw.uuid), draw],
    }))
  }

  remove(uuid: string) {
    this.patchState((state) => ({
      ...state,
      results: state.results.filter((x) => x.uuid !== uuid),
    }))
  }

  clear() {
    this.setState({ results: [], loading: false, offset: 0, count: 0 })
  }

  /** -------------------------
   * Effects with API calls
   * ------------------------- */
  loadRaffleDraws = this.effect<{ raffleItemUuid: string; offset: number }>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.state$),
      mergeMap(([{ raffleItemUuid, offset }, urlName, state]) => {
        this.patchState({ loading: true, offset })
        return this.rafflesApiService
          .getRaffleDraws(urlName, raffleItemUuid, offset, this.appConfig.itemsPageSize)
          .pipe(
            tapResponse({
              next: (response: GbPaginatedResponse<RaffleDrawsEntity>) => {
                this.patchState({
                  loading: false,
                  offset,
                  results: offset === 0 ? response.results : [...state.results, ...response.results],
                  count: response.count,
                })
              },
              error: (error: HttpErrorResponse) => {
                this.patchState({ offset: offset - this.appConfig.itemsPageSize })
                this.handleError(error)
              },
            }),
          )
      }),
    ),
  )

  readonly loadFirstRaffleDrawsPage = this.effect<{ raffleItemUuid: string }>((trigger$) =>
    trigger$.pipe(tap(({ raffleItemUuid }) => this.loadRaffleDraws({ raffleItemUuid, offset: 0 }))),
  )

  readonly loadNextRaffleDrawsPage = this.effect<{ raffleItemUuid: string }>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state$),
      filter(([, state$]) => !state$.loading && state$.results.length < state$.count),
      tap(([{ raffleItemUuid }, state$]) =>
        this.loadRaffleDraws({ raffleItemUuid, offset: state$.offset + this.appConfig.itemsPageSize }),
      ),
    ),
  )

  drawRaffle = this.effect<{
    item: string
    raffleItem: string
    prize: number
    onSuccess?: (raffleDraw: RaffleDrawsEntity) => void
  }>((trigger$) =>
    trigger$.pipe(
      tap(() => this.gbLoaderService.showLoaderDialog(this.LOADER_ID, 'LOADER.DRAWING_RAFFLE')),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ item, raffleItem, prize, onSuccess }, urlName]) =>
        this.rafflesApiService.drawRaffle(urlName, item, raffleItem, prize).pipe(
          tapResponse({
            next: (raffleDraw) => {
              this.upsert(raffleDraw)
              onSuccess?.(raffleDraw)
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(this.LOADER_ID)),
        ),
      ),
    ),
  )

  redrawRaffle = this.effect<{ raffleDraw: RaffleDrawsEntity; onSuccess?: (newDraw: RaffleDrawsEntity) => void }>(
    (trigger$) =>
      trigger$.pipe(
        tap(() => this.gbLoaderService.showLoaderDialog(this.LOADER_ID, 'LOADER.REDRAWING_RAFFLE')),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        switchMap(([{ raffleDraw, onSuccess }, urlName]) =>
          this.rafflesApiService.redrawRaffle(urlName, raffleDraw).pipe(
            tapResponse({
              next: (newDraw) => {
                this.upsert(newDraw)
                onSuccess?.(newDraw)
              },
              error: (error: HttpErrorResponse) => this.handleError(error),
            }),
            finalize(() => this.gbLoaderService.hideLoaderDialog(this.LOADER_ID)),
          ),
        ),
      ),
  )

  deleteRaffleDraw = this.effect<{ uuid: string }>((trigger$) =>
    trigger$.pipe(
      tap(() => this.gbLoaderService.showLoaderDialog(this.LOADER_ID)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ uuid }, urlName]) =>
        this.rafflesApiService.deleteRaffleDraw(urlName, uuid).pipe(
          tapResponse({
            next: () => this.remove(uuid),
            error: (error: HttpErrorResponse) => this.handleError(error),
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(this.LOADER_ID)),
        ),
      ),
    ),
  )

  bulkDrawRaffle = this.effect<{
    raffleItem: string
    onSuccess?: (raffleDraws: RaffleDrawsEntity[]) => void
  }>((trigger$) =>
    trigger$.pipe(
      tap(() => this.gbLoaderService.showLoaderDialog(this.LOADER_ID, 'LOADER.DRAWING_RAFFLE')),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ raffleItem, onSuccess }, urlName]) =>
        this.rafflesApiService.bulkDrawRaffle(urlName, raffleItem).pipe(
          tapResponse({
            next: (raffleDraws) => {
              raffleDraws.forEach((raffleDraw) => this.upsert(raffleDraw))
              onSuccess?.(raffleDraws)
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(this.LOADER_ID)),
        ),
      ),
    ),
  )

  /** -------------------------
   * Realtime updates
   * ------------------------- */

  private pendingUuids = new Set<string | number>()
  private realtimeDrawSubject = new Subject<RaffleDrawsEntity>()
  readonly realtimeDraw$ = this.realtimeDrawSubject.asObservable()

  readonly handleCreate = this.effect(() => {
    const rawActions$ = this.actions$.pipe(ofType(RafflesActions.createRaffleDrawForRealtime))

    return rawActions$.pipe(
      buffer(rawActions$.pipe(debounceTime(300))),
      filter((actions) => actions.length > 0),
      map((actions) => {
        const batchUuids = new Set<string | number>()
        return actions.filter(({ data }) => {
          if (this.pendingUuids.has(data.uuid) || batchUuids.has(data.uuid)) {
            return false
          }
          batchUuids.add(data.uuid)
          return true
        })
      }),
      filter((uniqueActions) => uniqueActions.length > 0),
      map((actions) => actions.sort((a, b) => Number(a.data.prize) - Number(b.data.prize))),
      concatMap((sortedActions) => from(sortedActions)),
      tap(({ data }) => this.pendingUuids.add(data.uuid)),
      concatMap((action) =>
        of(action).pipe(
          tap(({ data }) => this.store.dispatch(PresenterWidgetsActions.triggerRaffleDrawFromRealtime({ data }))),
          delay(3140),
          tap(({ data }) => {
            this.upsert(data)
            this.realtimeDrawSubject.next(data)
          }),
          finalize(() => this.pendingUuids.delete(action.data.uuid)),
        ),
      ),
    )
  })

  readonly handleDelete = this.effect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.deleteRaffleDrawForRealtime),
      tap(({ uuid }) => this.remove(uuid)),
    ),
  )

  /** -------------------------
   * Private helpers
   * ------------------------- */
  private handleError(error: HttpErrorResponse): void {
    const message = this.gbErrorMessageService.getErrorMessage(error.error)
    this.gbNotificationService.error(message || 'GENERIC.ERROR')
    this.patchState({ error, loading: false })
  }
}
